import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
    /** @type {HTMLDetailsElement} */
    get details() {
        const details = this.querySelector('details');

        if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

        return details;
    }

    /** @type {HTMLElement} */
    get summary() {
        const summary = this.details.querySelector('summary');

        if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

        return summary;
    }

    get #disableOnMobile() {
        return this.dataset.disableOnMobile === 'true';
    }

    get #disableOnDesktop() {
        return this.dataset.disableOnDesktop === 'true';
    }

    get #closeWithEscape() {
        return this.dataset.closeWithEscape === 'true';
    }

    #controller = new AbortController();

    connectedCallback() {
        const { signal } = this.#controller;

        this.#setDefaultOpenState();

        this.addEventListener('keydown', this.#handleKeyDown, { signal });
        this.summary.addEventListener('click', this.handleClick, { signal });
        this.details.addEventListener('toggle', this.#handleToggle, { signal });
        mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    }

    /**
     * Handles the disconnect event.
     */
    disconnectedCallback() {
        // Disconnect all the event listeners
        this.#controller.abort();
    }

    /**
     * Handles the click event.
     * @param {Event} event - The event.
     */
    handleClick = (event) => {
        const isMobile = isMobileBreakpoint();
        const isDesktop = !isMobile;

        // Stop default behaviour from the browser
        if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
            event.preventDefault();
            return;
        }
    };

    /**
     * Handles the media query change event.
     */
    #handleMediaQueryChange = () => {
        this.#setDefaultOpenState();
    };

    /**
     * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
     */
    #setDefaultOpenState() {
        const isMobile = isMobileBreakpoint();

        this.details.open =
            (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
            (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    }

    /**
     * Handles keydown events for the accordion
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    #handleKeyDown(event) {
        // Close the accordion when used as a menu
        if (event.key === 'Escape' && this.#closeWithEscape) {
            event.preventDefault();

            this.details.open = false;
            this.summary.focus();
        }
    }

    /**
     * Handles the toggle event - closes other accordions on mobile when one is opened
     */
    #handleToggle = () => {
        const isMobile = isMobileBreakpoint();

        // Only apply if mobile, accordion is being opened, not disabled on mobile, and has only-one-open attribute
        if (
            !isMobile ||
            !this.details.open ||
            this.#disableOnMobile ||
            !this.hasAttribute('only-one-open')
        )
            return;

        // Find the parent section
        const parentSection = this.closest('section, [class*="section"]');
        if (!parentSection) return;

        // Find all other accordion-custom elements with only-one-open in the same section
        const allAccordions = parentSection.querySelectorAll('accordion-custom[only-one-open]');

        allAccordions.forEach((accordion) => {
            // Skip the current accordion
            if (accordion === this) return;

            // Close other accordions
            const otherDetails = accordion.querySelector('details');
            if (otherDetails instanceof HTMLDetailsElement && otherDetails.open) {
                otherDetails.open = false;
            }
        });
    };
}

if (!customElements.get('accordion-custom')) {
    customElements.define('accordion-custom', AccordionCustom);
}
